type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';

interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: { start_param?: string; user?: { id: number; username?: string; first_name?: string; language_code?: string } };
  platform?: string;
  colorScheme?: string;
  ready(): void;
  expand(): void;
  isFullscreen?: boolean;
  requestFullscreen?(): void;
  disableVerticalSwipes?(): void;
  enableVerticalSwipes?(): void;
  openTelegramLink?(url: string): void;
  downloadFile?(params: { url: string; file_name: string }, callback: (accepted: boolean) => void): void;
  close?(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  BackButton?: { show(): void; hide(): void; onClick(callback: () => void): void; offClick(callback: () => void): void };
  safeAreaInset?: { top: number; bottom: number; left: number; right: number };
  contentSafeAreaInset?: { top: number; bottom: number; left: number; right: number };
  onEvent?(event: 'safeAreaChanged' | 'contentSafeAreaChanged' | 'fullscreenChanged' | 'fullscreenFailed' | 'viewportChanged', callback: (payload?: { error?: string; isFullscreen?: boolean; isStateStable?: boolean }) => void): void;
  offEvent?(event: 'safeAreaChanged' | 'contentSafeAreaChanged' | 'fullscreenChanged' | 'fullscreenFailed' | 'viewportChanged', callback: (payload?: { error?: string; isFullscreen?: boolean; isStateStable?: boolean }) => void): void;
  HapticFeedback?: {
    impactOccurred(style: HapticStyle): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
}

declare global {
  interface Window { Telegram?: { WebApp?: TelegramWebApp } }
}

const webApp = (): TelegramWebApp | undefined => window.Telegram?.WebApp;
let initializedApp: TelegramWebApp | undefined;
let desktopFullscreenRequested = false;
let desktopFullscreenUnsupported = false;
let desktopFullscreenAttempts = 0;
let desktopFullscreenRetryTimer: number | undefined;
let viewportSettleTimer: number | undefined;

export type TelegramClientAuthReason = 'init_data_present' | 'telegram_sdk_missing' | 'telegram_webapp_missing' | 'init_data_missing';

export interface TelegramReadiness {
  reason: TelegramClientAuthReason;
  initDataPresent: boolean;
  platform: string;
}

export const isTelegramDesktopPlatform = (platform: string): boolean => ['tdesktop', 'macos'].includes(platform.toLowerCase());

function setSafeAreas(): void {
  const root = document.documentElement;
  const safe = webApp()?.safeAreaInset;
  const content = webApp()?.contentSafeAreaInset;
  const set = (name: string, value = 0) => root.style.setProperty(name, `${Math.max(0, value)}px`);
  set('--tg-safe-top', safe?.top); set('--tg-safe-bottom', safe?.bottom); set('--tg-safe-left', safe?.left); set('--tg-safe-right', safe?.right);
  set('--tg-content-safe-top', content?.top); set('--tg-content-safe-bottom', content?.bottom);
  set('--tg-content-safe-left', content?.left); set('--tg-content-safe-right', content?.right);
}

function syncViewport(app: TelegramWebApp): void {
  const width = window.visualViewport?.width || window.innerWidth || document.documentElement.clientWidth;
  const isDesktopWidth = width >= 760;
  document.documentElement.dataset.appLayout = isDesktopWidth ? 'desktop' : 'mobile';
  document.documentElement.style.setProperty('--app-viewport-width', `${Math.max(0, width)}px`);
  try { app.expand(); } catch { /* Responsive CSS remains the fallback. */ }
  // A Telegram Desktop WebView can initially report a phone-sized width. Asking
  // for fullscreen must therefore depend on the host platform, not that width.
  if (!desktopFullscreenRequested && !desktopFullscreenUnsupported && desktopFullscreenAttempts < 2 && !app.isFullscreen && isTelegramDesktopPlatform(app.platform || '') && app.requestFullscreen) {
    desktopFullscreenRequested = true;
    desktopFullscreenAttempts += 1;
    try { app.requestFullscreen(); } catch { desktopFullscreenRequested = false; }
  }
}

function configureWebApp(app: TelegramWebApp): void {
  if (initializedApp === app) return;
  initializedApp = app;
  desktopFullscreenRequested = false;
  desktopFullscreenUnsupported = false;
  desktopFullscreenAttempts = 0;
  if (desktopFullscreenRetryTimer !== undefined) window.clearTimeout(desktopFullscreenRetryTimer);
  app.setHeaderColor?.('#17212B');
  app.setBackgroundColor?.('#17212B');
  app.ready();
  setSafeAreas();
  syncViewport(app);
  app.onEvent?.('safeAreaChanged', setSafeAreas);
  app.onEvent?.('contentSafeAreaChanged', setSafeAreas);
  app.onEvent?.('fullscreenChanged', (payload) => {
    if (payload?.isFullscreen) desktopFullscreenRequested = true;
    syncViewport(app);
  });
  app.onEvent?.('fullscreenFailed', (payload) => {
    if (payload?.error === 'UNSUPPORTED') { desktopFullscreenUnsupported = true; return; }
    if (desktopFullscreenAttempts >= 2) return;
    desktopFullscreenRequested = false;
    if (desktopFullscreenRetryTimer !== undefined) window.clearTimeout(desktopFullscreenRetryTimer);
    desktopFullscreenRetryTimer = window.setTimeout(() => syncViewport(app), 240);
  });
  app.onEvent?.('viewportChanged', (payload) => {
    syncViewport(app);
    if (viewportSettleTimer !== undefined) window.clearTimeout(viewportSettleTimer);
    if (payload?.isStateStable === false) viewportSettleTimer = window.setTimeout(() => syncViewport(app), 120);
  });
  window.addEventListener('resize', () => syncViewport(app), { passive: true });
  window.visualViewport?.addEventListener('resize', () => syncViewport(app), { passive: true });
}

async function waitForTelegramSdk(): Promise<void> {
  if (webApp()) return;
  const script = document.getElementById('telegram-web-app-sdk');
  await new Promise<void>((resolve) => {
    let settled = false;
    const done = () => { if (!settled) { settled = true; resolve(); } };
    script?.addEventListener('load', done, { once: true });
    script?.addEventListener('error', done, { once: true });
    if (document.readyState === 'complete') queueMicrotask(done);
    else window.addEventListener('load', done, { once: true });
  });
}

function readiness(): TelegramReadiness {
  const app = webApp();
  const reason: TelegramClientAuthReason = !window.Telegram
    ? 'telegram_sdk_missing'
    : !app ? 'telegram_webapp_missing' : app.initData ? 'init_data_present' : 'init_data_missing';
  return { reason, initDataPresent: Boolean(app?.initData), platform: app?.platform || 'web' };
}

export const telegram = {
  isAvailable: () => Boolean(webApp()?.initData),
  initData: () => webApp()?.initData || '',
  user: () => webApp()?.initDataUnsafe?.user,
  languageCode: () => webApp()?.initDataUnsafe?.user?.language_code || '',
  startParam: () => webApp()?.initDataUnsafe?.start_param || new URLSearchParams(location.search).get('tgWebAppStartParam') || '',
  platform: () => webApp()?.platform || 'web',
  supportsNativeDownload: () => typeof webApp()?.downloadFile === 'function',
  downloadFile: (value: { url: string; file_name: string }): Promise<boolean> => new Promise((resolve) => {
    const app = webApp();
    if (!app?.downloadFile) { resolve(false); return; }
    try { app.downloadFile(value, resolve); } catch { resolve(false); }
  }),
  initialize: () => {
    const app = webApp();
    if (!app) return;
    configureWebApp(app);
  },
  resyncViewport: () => {
    const app = webApp();
    if (app) syncViewport(app);
  },
  awaitReady: async (): Promise<TelegramReadiness> => {
    await waitForTelegramSdk();
    const app = webApp();
    if (app) configureWebApp(app);
    await Promise.resolve();
    return readiness();
  },
  diagnostics: readiness,
  lockEditorGestures: (locked: boolean) => {
    try { if (locked) webApp()?.disableVerticalSwipes?.(); else webApp()?.enableVerticalSwipes?.(); }
    catch { /* Safe no-op outside supported clients. */ }
  },
  haptic: (kind: 'selection' | 'success' | 'error' | HapticStyle = 'light') => {
    const haptic = webApp()?.HapticFeedback;
    if (haptic) {
      if (kind === 'selection') haptic.selectionChanged();
      else if (kind === 'success' || kind === 'error') haptic.notificationOccurred(kind);
      else haptic.impactOccurred(kind);
      return;
    }
    if ('vibrate' in navigator && ['success', 'error', 'medium', 'heavy'].includes(kind)) navigator.vibrate(kind === 'error' ? [20, 30, 20] : 12);
  },
  openTelegramLink: (url: string) => {
    if (webApp()?.openTelegramLink) webApp()!.openTelegramLink!(url);
    else window.location.assign(url);
  },
  openBot: (url: string) => {
    if (webApp()?.openTelegramLink) webApp()!.openTelegramLink!(url);
    else window.location.assign(url);
  },
  close: () => { try { webApp()?.close?.(); } catch { /* Browser fallback is a no-op. */ } },
  showBack: (handler: () => void) => {
    const button = webApp()?.BackButton;
    if (!button) return () => undefined;
    button.show();
    button.onClick(handler);
    return () => { button.offClick(handler); button.hide(); };
  },
};
